import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from '@/Public/ChatBot/Types';

interface UseChatScrollReturn {
    readonly scrollAreaRef: React.RefObject<HTMLDivElement>;
    readonly lastMessageRef: React.RefObject<HTMLDivElement>;
    readonly typingRef: React.RefObject<HTMLDivElement>;
    readonly bottomSentinelRef: React.RefObject<HTMLDivElement>;
    readonly showScrollButton: boolean;
    readonly scrollToBottom: () => void;
    readonly resetScroll: () => void;
}

export function useChatScroll(
    messages: readonly ChatMessage[],
    isLoading: boolean,
    isOpen: boolean,
): UseChatScrollReturn {
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const lastMessageRef = useRef<HTMLDivElement>(null);
    const typingRef = useRef<HTMLDivElement>(null);
    const bottomSentinelRef = useRef<HTMLDivElement>(null);
    const prevMessageCountRef = useRef(messages.length);
    const isUserScrolledRef = useRef(false);
    const [showScrollButton, setShowScrollButton] = useState(false);

    const getScrollViewport = (): Element | null => {
        return scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]') ?? null;
    };

    const scrollToBottom = (): void => {
        const viewport = getScrollViewport();
        if (viewport) {
            viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
        }
        isUserScrolledRef.current = false;
        setShowScrollButton(false);
    };

    const resetScroll = (): void => {
        isUserScrolledRef.current = false;
        setShowScrollButton(false);
    };

    useEffect(() => {
        const sentinel = bottomSentinelRef.current;
        if (!sentinel) return;

        const viewport = getScrollViewport();

        const observer = new IntersectionObserver(
            ([entry]) => {
                const atBottom = entry.isIntersecting;
                isUserScrolledRef.current = !atBottom;
                setShowScrollButton(!atBottom);
            },
            {
                root: viewport,
                rootMargin: '40px',
                threshold: 0,
            }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [isOpen]);

    useEffect(() => {
        const prevCount = prevMessageCountRef.current;
        const currentCount = messages.length;
        prevMessageCountRef.current = currentCount;

        if (currentCount === prevCount) {
            if (isLoading && !isUserScrolledRef.current) {
                requestAnimationFrame(() => {
                    typingRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
                });
            }
            return;
        }

        if (isUserScrolledRef.current) return;

        const lastMessage = messages[currentCount - 1];
        if (!lastMessage) return;

        if (lastMessage.role === 'user') {
            requestAnimationFrame(() => {
                const vp = getScrollViewport();
                if (vp) {
                    vp.scrollTo({ top: vp.scrollHeight, behavior: 'smooth' });
                }
                isUserScrolledRef.current = false;
                setShowScrollButton(false);
            });
        } else {
            requestAnimationFrame(() => {
                lastMessageRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
            });
        }
    }, [messages, isLoading]);

    return {
        scrollAreaRef,
        lastMessageRef,
        typingRef,
        bottomSentinelRef,
        showScrollButton,
        scrollToBottom,
        resetScroll,
    };
}
